import axios from 'axios';
import endpoint from 'api/config/endpoint';
import { clearActingProducer } from 'helpers/actingProducer';

/*
 * Sessão com refresh token.
 *
 * O access token continua no localStorage, mas dura 15 minutos. Quem renova é o
 * refresh token, que a API grava num cookie HttpOnly (o JavaScript não o lê) e
 * só aceita junto do header X-Auth-Refresh. Por isso só as chamadas de login,
 * refresh e logout vão com `withCredentials` e com o header — o resto da API
 * segue igual.
 */

export const REFRESH_HEADER = 'X-Auth-Refresh';
const CLIENT = 'web';
const LOCK_NAME = 'whizr-session-refresh';

/** Config do axios para as chamadas que recebem ou mandam o cookie do refresh. */
export const sessionRequestConfig = () => ({
    withCredentials: true,
    headers: { [REFRESH_HEADER]: CLIENT },
});

let inFlight = null;

/**
 * Troca o refresh token por um access token novo e grava no localStorage.
 *
 * Várias requisições recebendo 401 juntas disparam um único refresh, e entre
 * abas o `navigator.locks` serializa: quem chega depois encontra o token já
 * trocado pela outra aba e só o reaproveita, sem gastar o refresh token de novo.
 *
 * @param {string|null} failedToken - o access token que levou o 401
 * @returns {Promise<string|null>} o token novo, ou null se a sessão acabou
 */
export function refreshSession(failedToken) {
    if (!inFlight) {
        inFlight = withLock(() => doRefresh(failedToken))
            .finally(() => { inFlight = null; });
    }

    return inFlight;
}

/**
 * Sai de verdade: a API revoga o refresh token e apaga o cookie. O estado
 * local é limpo antes, e de forma síncrona — sair nunca depende da rede.
 *
 * @returns {Promise<void>} resolve quando a API respondeu (ou falhou); só
 * precisa ser aguardada por quem recarrega a página logo em seguida.
 */
export function endSession() {
    const token = localStorage.getItem('token');

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearActingProducer();

    return postSession('users/logout', token).catch(() => {
        // Sem rede ou token já inválido: o cookie vence sozinho.
    });
}

async function doRefresh(failedToken) {
    const current = localStorage.getItem('token');

    // Outra aba renovou enquanto esta esperava o lock.
    if (current && current !== failedToken) {
        return current;
    }

    try {
        // A API confere que o access token (mesmo vencido) é do dono do
        // refresh token — é o que impede uma aba de "Entrar como" de virar
        // a sessão real do navegador.
        const { data } = await postSession('users/refresh-token', failedToken);

        localStorage.setItem('token', data.accessToken);

        return data.accessToken;
    } catch (error) {
        return null;
    }
}

/*
 * Axios puro, fora da instância de `api()`: um 401 aqui não pode cair no
 * interceptor que tenta renovar a sessão.
 */
function postSession(path, accessToken) {
    const { headers, ...config } = sessionRequestConfig();

    return axios.post(`${endpoint.get()}${path}`, null, {
        ...config,
        headers: {
            ...headers,
            accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
    });
}

function withLock(callback) {
    if (navigator.locks?.request) {
        return navigator.locks.request(LOCK_NAME, callback);
    }

    return callback();
}
